/**
 * ReportAst — the FE mirror of `hub.reports.ast` (ADR-0061) + the pure
 * helpers the designer canvas edits it with.
 *
 * Bands are addressed by PATH (index chain through `detail_report.bands`),
 * controls by (band path, index) — same addressing idea as the Type tree
 * helpers in ReportTypesPage. All edits are immutable rewrites. Keep the
 * TYPE vocabulary in lockstep with the backend pydantic models — the save
 * re-validates server-side, this mirror is for authoring ergonomics.
 */
import type { ReportTypeNode } from "@/lib/api-reports";

// ---------------------------------------------------------------------------
// Types (mirror of hub/reports/ast.py)
// ---------------------------------------------------------------------------

export const PAGE_FORMATS: Record<string, [number, number]> = {
  A4: [210, 297],
  A5: [148, 210],
  letter: [215.9, 279.4],
};

export const BAND_KINDS = [
  "report_header",
  "page_header",
  "group_header",
  "detail",
  "detail_report",
  "group_footer",
  "page_footer",
  "report_footer",
] as const;
export type BandKind = (typeof BAND_KINDS)[number];

export const AGG_OPS = ["sum", "avg", "count", "min", "max"] as const;
export type AggOp = (typeof AGG_OPS)[number];

/** Bands where a `field.agg` summary is legal (mirror `_AGG_BANDS`). */
export const AGG_BANDS: ReadonlySet<BandKind> = new Set([
  "group_footer",
  "report_footer",
  "report_header",
]);

export interface ControlStyle {
  font_family?: string | null;
  font_size?: number | null;
  bold?: boolean;
  italic?: boolean;
  align?: "left" | "center" | "right" | "justify" | null;
  color?: string | null;
  background?: string | null;
  border_width?: number | null;
  border_color?: string | null;
  padding?: number | null;
}

export interface FieldFormat {
  kind: "text" | "number" | "currency" | "date" | "datetime";
  decimals?: number;
  symbol?: string;
  pattern?: string | null;
}

interface BaseControl {
  x: number;
  y: number;
  w: number;
  h: number;
  style?: ControlStyle;
  visible_when?: string | null;
}

export interface LabelControl extends BaseControl {
  kind: "label";
  text: string;
}
export interface FieldControl extends BaseControl {
  kind: "field";
  bind: string;
  format?: FieldFormat;
  agg?: AggOp | null;
}
export interface ImageControl extends BaseControl {
  kind: "image";
  src?: string | null;
  bind?: string | null;
  fit?: "contain" | "cover" | "fill";
}
export interface LineControl extends BaseControl {
  kind: "line";
  direction?: "horizontal" | "vertical";
}
export interface ShapeControl extends BaseControl {
  kind: "shape";
  radius?: number;
}
export interface PageInfoControl extends BaseControl {
  kind: "page_info";
  info?: "page_number" | "page_count" | "page_x_of_y" | "print_date";
}
/** Draggable page-break marker: the band splits at this control's `y`
 *  (content below starts on a new page). Rejected in page_header/footer. */
export interface PageBreakControl extends BaseControl {
  kind: "page_break";
}
/** Boolean field as a ☑/☐ glyph + optional label. */
export interface CheckboxControl extends BaseControl {
  kind: "checkbox";
  bind: string;
  text?: string;
}
/** Multi-line text with minimal inline markup (**bold**, *italic*, newlines). */
export interface RichTextControl extends BaseControl {
  kind: "rich_text";
  text: string;
}
/** QR / Code128 / EAN-13 over a bound field or a static value (server-side
 *  generation — segno / python-barcode). */
export interface BarcodeControl extends BaseControl {
  kind: "barcode";
  symbology?: "qr" | "code128" | "ean13";
  bind?: string | null;
  value?: string | null;
  show_text?: boolean;
}
export interface TableColumn {
  header?: string;
  bind: string;
  width_mm?: number | null;
  align?: "left" | "center" | "right" | null;
  format?: FieldFormat;
}
export interface TableControl extends BaseControl {
  kind: "table";
  bind: string;
  columns: TableColumn[];
}

export type ReportControl =
  | LabelControl
  | FieldControl
  | ImageControl
  | LineControl
  | ShapeControl
  | PageInfoControl
  | PageBreakControl
  | CheckboxControl
  | RichTextControl
  | BarcodeControl
  | TableControl;

export interface GroupSpec {
  by: string;
  sort?: "asc" | "desc";
}

export interface ReportBand {
  kind: BandKind;
  height_mm: number;
  auto_grow?: boolean;
  keep_together?: boolean;
  page_break?: "none" | "before" | "after";
  controls: ReportControl[];
  /** detail_report only. */
  bind?: string | null;
  bands?: ReportBand[];
}

export interface LabelGrid {
  cols: number;
  rows: number;
  label_w: number;
  label_h: number;
  gap_x?: number;
  gap_y?: number;
}

export interface PageSetup {
  kind: "document" | "labels";
  format: string;
  orientation: "portrait" | "landscape";
  custom_w?: number | null;
  custom_h?: number | null;
  margins: { top: number; right: number; bottom: number; left: number };
  labels?: LabelGrid | null;
}

export interface ReportLayout {
  page_setup: PageSetup;
  bands: ReportBand[];
  groups: GroupSpec[];
}

// ---------------------------------------------------------------------------
// Normalization + defaults
// ---------------------------------------------------------------------------

export function defaultPageSetup(): PageSetup {
  return {
    kind: "document",
    format: "A4",
    orientation: "portrait",
    margins: { top: 10, right: 10, bottom: 10, left: 10 },
  };
}

/** Coerce a persisted layout (possibly `{}` / partial) into a full shape the
 *  designer can edit — never throws, the SAVE re-validates server-side. */
/** Canonical top-to-bottom band order on the printed page. The compiler
 *  routes bands BY KIND (array order only matters within a kind), so the
 *  designer must SHOW them where they print — a report_header added last
 *  must not render below the page footer. */
export const BAND_DISPLAY_ORDER: Record<BandKind, number> = {
  page_header: 0,
  report_header: 1,
  group_header: 2,
  detail: 3,
  detail_report: 4,
  group_footer: 5,
  report_footer: 6,
  page_footer: 7,
};

/** Stable-sort bands into canonical print order, recursing into nested
 *  detail_report bands. Same-kind bands keep their authored order. */
export function sortBands(bands: ReportBand[]): ReportBand[] {
  const sorted = [...bands].sort((a, b) => BAND_DISPLAY_ORDER[a.kind] - BAND_DISPLAY_ORDER[b.kind]);
  return sorted.map((b) => (b.bands?.length ? { ...b, bands: sortBands(b.bands) } : b));
}

export function normalizeLayout(raw: Record<string, unknown> | null | undefined): ReportLayout {
  const r = (raw ?? {}) as Partial<ReportLayout>;
  return {
    page_setup: { ...defaultPageSetup(), ...(r.page_setup ?? {}) },
    bands: sortBands(Array.isArray(r.bands) ? (r.bands as ReportBand[]) : []),
    groups: Array.isArray(r.groups) ? (r.groups as GroupSpec[]) : [],
  };
}

/** The physical page (w, h) in mm, orientation applied — mirror of
 *  `PageSetup.size_mm` so the canvas geometry can't drift from the renderer. */
export function pageSizeMm(setup: PageSetup): [number, number] {
  let w: number;
  let h: number;
  if (setup.format === "custom") {
    w = setup.custom_w || 210;
    h = setup.custom_h || 297;
  } else {
    [w, h] = PAGE_FORMATS[setup.format] ?? PAGE_FORMATS.A4;
  }
  return setup.orientation === "landscape" ? [h, w] : [w, h];
}

/** The band lane width in mm: page content width (document) or the label
 *  cell width (labels mode). */
export function laneWidthMm(setup: PageSetup): number {
  if (setup.kind === "labels" && setup.labels) return setup.labels.label_w;
  const [w] = pageSizeMm(setup);
  return Math.max(10, w - setup.margins.left - setup.margins.right);
}

/** The band's RENDERED height: with `auto_grow` (the default) it grows to the
 *  controls' bottom edge — the compiler's min-height formula, mirrored here so
 *  a control dragged below `height_mm` stays visible in the canvas instead of
 *  vanishing under the next lane (WYSIWYG parity). A fixed band (`auto_grow:
 *  false`) clips at `height_mm`, like print. */
export function bandVisualHeightMm(band: ReportBand): number {
  if (band.auto_grow === false) return band.height_mm;
  return Math.max(band.height_mm, ...band.controls.map((c) => c.y + c.h));
}

export function newBand(kind: BandKind): ReportBand {
  const band: ReportBand = { kind, height_mm: 15, auto_grow: true, controls: [] };
  if (kind === "detail_report") {
    band.bind = null;
    band.bands = [{ kind: "detail", height_mm: 10, auto_grow: true, controls: [] }];
  }
  return band;
}

export function newControl(kind: ReportControl["kind"], x = 5, y = 2): ReportControl {
  const base = { x, y, w: 40, h: 6 };
  switch (kind) {
    case "label":
      return { kind, ...base, text: "Etichetta" };
    case "field":
      return { kind, ...base, bind: "" };
    case "image":
      return { kind, ...base, w: 25, h: 15, src: "" };
    case "line":
      return { kind, ...base, w: 60, h: 1, direction: "horizontal" };
    case "shape":
      return { kind, ...base, w: 30, h: 12 };
    case "page_info":
      return { kind, ...base, w: 25, info: "page_x_of_y" };
    case "page_break":
      // full-width-ish marker; only y matters at render time.
      return { kind, ...base, x: 0, w: 80, h: 4 };
    case "checkbox":
      return { kind, ...base, bind: "", text: "" };
    case "rich_text":
      return { kind, ...base, w: 80, h: 20, text: "Testo…" };
    case "barcode":
      return { kind, ...base, w: 30, h: 30, symbology: "qr", bind: null, value: "" };
    case "table":
      return { kind, ...base, w: 120, h: 25, bind: "", columns: [] };
  }
}

// ---------------------------------------------------------------------------
// Path addressing (bands nest only through detail_report.bands)
// ---------------------------------------------------------------------------

export type BandPath = number[];

export function bandAt(bands: ReportBand[], path: BandPath): ReportBand | undefined {
  let cur: ReportBand | undefined;
  let list = bands;
  for (const idx of path) {
    cur = list[idx];
    if (!cur) return undefined;
    list = cur.bands ?? [];
  }
  return cur;
}

export function updateBandAt(
  bands: ReportBand[],
  path: BandPath,
  patch: Partial<ReportBand> | ((b: ReportBand) => ReportBand),
): ReportBand[] {
  const [head, ...rest] = path;
  return bands.map((b, i) => {
    if (i !== head) return b;
    if (rest.length === 0) return typeof patch === "function" ? patch(b) : { ...b, ...patch };
    return { ...b, bands: updateBandAt(b.bands ?? [], rest, patch) };
  });
}

export function removeBandAt(bands: ReportBand[], path: BandPath): ReportBand[] {
  const [head, ...rest] = path;
  if (rest.length === 0) return bands.filter((_, i) => i !== head);
  return bands.map((b, i) => (i === head ? { ...b, bands: removeBandAt(b.bands ?? [], rest) } : b));
}

export function updateControlAt(
  bands: ReportBand[],
  path: BandPath,
  index: number,
  patch: Partial<ReportControl>,
): ReportBand[] {
  return updateBandAt(bands, path, (b) => ({
    ...b,
    controls: b.controls.map((c, i) => (i === index ? ({ ...c, ...patch } as ReportControl) : c)),
  }));
}

export function addControlAt(
  bands: ReportBand[],
  path: BandPath,
  control: ReportControl,
): ReportBand[] {
  return updateBandAt(bands, path, (b) => ({ ...b, controls: [...b.controls, control] }));
}

export function removeControlAt(bands: ReportBand[], path: BandPath, index: number): ReportBand[] {
  return updateBandAt(bands, path, (b) => ({
    ...b,
    controls: b.controls.filter((_, i) => i !== index),
  }));
}

// ---------------------------------------------------------------------------
// Scope resolution — which Type nodes a band's controls may bind
// ---------------------------------------------------------------------------

/** The fields addressable INSIDE a band at `path`: the root Type fields, or —
 *  inside a detail_report chain — the bound collection's children (mirror of
 *  the backend's per-scope referential validation). Unresolvable (a
 *  detail_report without a bind yet) → []. */
export function scopeNodesFor(
  bands: ReportBand[],
  path: BandPath,
  typeNodes: ReportTypeNode[],
): ReportTypeNode[] {
  let scope = typeNodes;
  let list = bands;
  for (const idx of path.slice(0, -1)) {
    const band = list[idx];
    if (!band || band.kind !== "detail_report") return scope;
    const coll = scope.find((n) => n.id === band.bind && n.type === "collection");
    if (!coll) return [];
    scope = coll.children ?? [];
    list = band.bands ?? [];
  }
  return scope;
}

/** The collection nodes of a scope (detail_report / table bind targets). */
export function collectionsOf(nodes: ReportTypeNode[]): ReportTypeNode[] {
  return nodes.filter((n) => n.type === "collection");
}

/** One dotted-addressable entry of a scope (the backend `_scope_maps` twin). */
export interface ScopeEntry {
  /** The DOTTED binding key (`test.campo_3`) — what a control's `bind` stores. */
  id: string;
  node: ReportTypeNode;
}

/** Flatten a scope into its addressable binding entries: nodes inside an
 *  `object` subtree are reachable via the dotted path. The walk does NOT
 *  descend into a `collection` — its children belong to the CHILD row scope
 *  (a `detail_report`/`table` re-scopes there). Mirror of the backend
 *  `ast._scope_maps`; keep the two in sync. */
export function flattenScope(nodes: ReportTypeNode[], prefix = ""): ScopeEntry[] {
  const out: ScopeEntry[] = [];
  for (const n of nodes) {
    const id = `${prefix}${n.id}`;
    out.push({ id, node: n });
    if (n.type === "object") out.push(...flattenScope(n.children ?? [], `${id}.`));
  }
  return out;
}

/** The dotted collection entries of a scope (detail_report / table binds). */
export function flatCollections(nodes: ReportTypeNode[]): ScopeEntry[] {
  return flattenScope(nodes).filter((e) => e.node.type === "collection");
}

/** A `table` control seeded from a collection: one column per scalar child
 *  (dotted through nested objects). `bind` is the collection's DOTTED id in
 *  the parent scope (defaults to the flat id for top-level collections). */
export function tableFromCollection(
  coll: ReportTypeNode,
  x: number,
  y: number,
  bind: string = coll.id,
): TableControl {
  const columns: TableColumn[] = flattenScope(coll.children ?? [])
    .filter((e) => e.node.type !== "object" && e.node.type !== "collection")
    .map((e) => ({ header: e.node.label || e.id, bind: e.id }));
  return {
    kind: "table",
    x,
    y,
    w: 140,
    h: 25,
    bind,
    columns: columns.length ? columns : [{ header: coll.id, bind: "" }],
  };
}

// ---------------------------------------------------------------------------
// Multi-select operations (S4b hardening) — align / distribute / clone
// ---------------------------------------------------------------------------

export type AlignMode = "left" | "center_h" | "right" | "top" | "center_v" | "bottom";

/** Align the selected controls of ONE band to the first selected one (the
 *  anchor — the DevExpress convention). Pure, path-addressed. */
export function alignControls(
  bands: ReportBand[],
  path: BandPath,
  indices: number[],
  mode: AlignMode,
): ReportBand[] {
  if (indices.length < 2) return bands;
  return updateBandAt(bands, path, (b) => {
    const anchor = b.controls[indices[0]];
    if (!anchor) return b;
    const idx = new Set(indices);
    return {
      ...b,
      controls: b.controls.map((c, i) => {
        if (!idx.has(i) || i === indices[0]) return c;
        switch (mode) {
          case "left":
            return { ...c, x: anchor.x };
          case "right":
            return { ...c, x: Math.max(0, anchor.x + anchor.w - c.w) };
          case "center_h":
            return { ...c, x: Math.max(0, anchor.x + anchor.w / 2 - c.w / 2) };
          case "top":
            return { ...c, y: anchor.y };
          case "bottom":
            return { ...c, y: Math.max(0, anchor.y + anchor.h - c.h) };
          case "center_v":
            return { ...c, y: Math.max(0, anchor.y + anchor.h / 2 - c.h / 2) };
        }
      }),
    };
  });
}

/** Space the selected controls evenly between the first and last (by axis
 *  position). Needs ≥3 controls to change anything. */
export function distributeControls(
  bands: ReportBand[],
  path: BandPath,
  indices: number[],
  axis: "h" | "v",
): ReportBand[] {
  if (indices.length < 3) return bands;
  return updateBandAt(bands, path, (b) => {
    const picked = indices
      .map((i) => ({ i, c: b.controls[i] }))
      .filter((p): p is { i: number; c: ReportControl } => !!p.c)
      .sort((a, z) => (axis === "h" ? a.c.x - z.c.x : a.c.y - z.c.y));
    if (picked.length < 3) return b;
    const first = picked[0].c;
    const last = picked[picked.length - 1].c;
    const span = axis === "h" ? last.x + last.w - first.x : last.y + last.h - first.y;
    const total = picked.reduce((s, p) => s + (axis === "h" ? p.c.w : p.c.h), 0);
    const gap = Math.max(0, (span - total) / (picked.length - 1));
    const moves = new Map<number, number>();
    let cursor = axis === "h" ? first.x : first.y;
    for (const p of picked) {
      moves.set(p.i, Math.round(cursor * 10) / 10);
      cursor += (axis === "h" ? p.c.w : p.c.h) + gap;
    }
    return {
      ...b,
      controls: b.controls.map((c, i) => {
        const pos = moves.get(i);
        if (pos === undefined) return c;
        return axis === "h" ? { ...c, x: pos } : { ...c, y: pos };
      }),
    };
  });
}

/** Deep-clone controls with a small offset (paste / duplicate). */
export function cloneControls(controls: ReportControl[], offsetMm = 3): ReportControl[] {
  return controls.map(
    (c) =>
      ({
        ...(JSON.parse(JSON.stringify(c)) as ReportControl),
        x: c.x + offsetMm,
        y: c.y + offsetMm,
      }) as ReportControl,
  );
}
